import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser as amplifyGetCurrentUser,
  confirmSignIn,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
} from "aws-amplify/auth";
import { configureAWS } from "./config";
import toast from "react-hot-toast";
import { verifyUser } from "./lambda";
import { AUTH_ROUTE } from "app/constants/route";

// Initialize AWS Amplify
configureAWS();

const region = process.env.REACT_APP_AWS_REGION;
const identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID;

// Validate AWS configuration
if (!region || !identityPoolId) {
  console.error("AWS configuration is missing:", {
    region,
    identityPoolId: identityPoolId ? "***" : undefined,
  });
  throw new Error("AWS configuration is missing. Check environment variables.");
}

// Create credentials provider using Cognito Identity Pool
let cognitoClient: CognitoIdentityClient;
try {
  cognitoClient = new CognitoIdentityClient({ region });
  console.log("Cognito Identity client created successfully");
} catch (error) {
  console.error("Failed to create Cognito Identity client:", error);
  throw new Error(
    `Failed to initialize Cognito client: ${
      error instanceof Error ? error.message : "Unknown error"
    }`
  );
}

// Create a credentials provider that enforces authentication
export const credentialsProvider = async () => {
  try {
    const id = process.env.REACT_APP_USER_POOL_ID;

    // Check if the user is authenticated first
    const session = await fetchAuthSession();
    if (!session) {
      console.error("Failed to fetch authentication session");
      throw new Error("Failed to fetch authentication session");
    }

    cognitoClient = new CognitoIdentityClient({ region });

    if (!session.tokens) {
      console.error("Authentication required to access AWS resources");
      throw new Error("Authentication required to access AWS resources");
    }

    const idpConfig = {
      client: cognitoClient,
      identityPoolId,
      logins: {
        [`cognito-idp.${region}.amazonaws.com/${id}`]:
          session.tokens.idToken?.toString() || "",
      },
    };

    // Only provide credentials if authenticated
    return fromCognitoIdentityPool(idpConfig)();
  } catch (error) {
    console.error("Error refreshing credentials:", error);
    throw new Error("Failed to refresh AWS credentials");
  }
};
/**
 * Refresh AWS credentials to ensure they reflect the current authentication state
 * This is critical for transitioning from unauthenticated to authenticated role
 */
export const refreshCredentials = async (): Promise<void> => {
  try {
    console.log("Refreshing AWS credentials...");

    // Fetch current authentication session
    const session = await fetchAuthSession();
    if (!session) {
      console.error("Failed to fetch authentication session");
      throw new Error("Failed to fetch authentication session");
    }

    // Log detailed info for debugging
    console.log("Auth session refreshed:", {
      hasTokens: !!session.tokens,
      hasIdentityId: !!session.identityId,
    });

    if (!session.tokens) {
      console.error(
        "Cannot refresh credentials: No authentication tokens found"
      );
      throw new Error("Authentication required to refresh AWS credentials");
    }

    // Force credentials refresh by clearing cache and requesting new credentials
    await credentialsProvider();

    console.log("AWS credentials refreshed successfully");
  } catch (error) {
    console.error("Error refreshing credentials:", error);
    throw new Error("Failed to refresh AWS credentials");
  }
};

// Function to get the identity ID with enhanced error handling
export const getIdentityId = async (): Promise<string> => {
  try {
    // Check if the user is authenticated first
    const session = await fetchAuthSession();
    if (!session) {
      console.error("Failed to fetch authentication session");
      throw new Error("Failed to fetch authentication session");
    }
    if (!session.tokens) {
      console.error("Authentication required to get identity ID");
      throw new Error("Authentication required");
    }

    console.log("Getting credentials from Cognito Identity Pool...");
    const credentials = await credentialsProvider();

    if (!credentials.identityId) {
      console.error("No identity ID returned from Cognito");
      throw new Error("Authentication failed: No identity ID returned");
    }

    console.log("Successfully obtained identity ID");
    return credentials.identityId;
  } catch (error) {
    console.error("Failed to get identity ID:", error);

    // Provide more specific error messages based on error type
    if (error instanceof Error) {
      if (error.message === "Authentication required") {
        throw new Error("Authentication required to access AWS resources");
      } else if (error.message.includes("CredentialsProviderError")) {
        throw new Error(
          "Authentication failed: Could not obtain credentials from Cognito Identity Pool"
        );
      } else if (error.message.includes("NetworkError")) {
        throw new Error(
          "Authentication failed: Network error when connecting to AWS services"
        );
      } else if (error.message.includes("NotAuthorizedException")) {
        throw new Error(
          "Authentication failed: Not authorized to access the Identity Pool"
        );
      } else if (
        error.message.includes("Unauthenticated access is not supported")
      ) {
        throw new Error("Authentication required to access AWS resources");
      } else {
        throw new Error(`Authentication failed: ${error.message}`);
      }
    }

    throw new Error("Authentication failed: Unknown error occurred");
  }
};

/**
 * Sign in with username and password
 *
 * @param params Object containing username and password
 * @returns Object with isSignedIn flag and user data
 */
export const signIn = async ({
  username,
  password,
}: {
  username: string;
  password: string;
}) => {
  try {
    // First try to verify user status
    try {
      const { verifyUser } = await import("./lambda");
      const verifyResult = await verifyUser(username);
      
      if (!verifyResult.valid) {
        console.log("User not verified, attempting verification...");
        // Try to verify the user
        const retryVerify = await verifyUser(username);
        if (!retryVerify.valid) {
          console.warn("User verification failed, attempting sign in anyway:", retryVerify.error);
        } else {
          console.log("User verification successful");
        }
      }
    } catch (verifyError) {
      // Log error but continue with sign in
      console.warn("Error during user verification, attempting sign in anyway:", verifyError);
    }

    // Proceed with sign in regardless of verification status
    const result = await amplifySignIn({
      username,
      password,
    });

    console.log("Sign in result:", result);

    // Check if user needs to change password
    if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      console.log("New password required for user");
      return {
        isSignedIn: false,
        requiresNewPassword: true,
        user: result,
      };
    }

    console.log("User signed in successfully");

    // Refresh credentials after successful sign-in
    try {
      await refreshCredentials();
      console.log("Credentials refreshed after sign-in");
    } catch (refreshError) {
      console.warn(
        "Failed to refresh credentials after sign-in:",
        refreshError
      );
      // Continue despite refresh error - we'll try again later
    }

    return {
      isSignedIn: true,
      requiresNewPassword: false,
      user: result,
    };
  } catch (error) {
    console.error("Error signing in:", error);
    handleAuthError(error);
    throw error;
  }
};

/**
 * Sign in anonymously (similar to Firebase anonymous authentication)
 * This creates a temporary user that can be later upgraded to a full account
 *
 * @returns User data or null if anonymous sign in failed
 */
export const signInAnonymously = async () => {
  try {
    // For development/migration purposes, we're just creating a temporary user ID
    // In a real implementation, this would use Cognito's guest access or a similar feature
    const tempUserId = "anon_" + Math.random().toString(36).substring(2, 15);

    console.log("Anonymous sign in, creating temporary user:", tempUserId);

    return {
      isAuthenticated: true,
      userId: tempUserId,
      isAnonymous: true,
      displayName: "Anonymous User",
      email: null,
      username: null,
    };
  } catch (error) {
    console.error("Anonymous Sign In Error:", error);
    toast.error("Failed to sign in anonymously. Please try again.");
    return null;
  }
};

/**
 * Enhanced sign in that handles "already signed in" errors
 * This function will attempt to sign in normally, but if it encounters
 * an "already signed in" error, it will force a clean sign out and retry.
 */
export const enhancedSignIn = async (
  username: string,
  password: string
): Promise<unknown> => {
  try {
    // First attempt normal sign in
    const result = await signIn({ username, password });
    // Note: signIn already refreshes credentials, but we'll ensure it's done
    return result;
  } catch (error) {
    // If we get "already signed in" error, force clean sign out and retry
    if (
      error instanceof Error &&
      (error.message.includes("already a signed in user") ||
        error.name === "UserAlreadyAuthenticatedException")
    ) {
      console.log(
        "Detected 'already signed in' error. Cleaning up auth state and retrying..."
      );

      // Force cleanup
      await forceCleanSignOut();

      // Wait a brief moment for cleanup to complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Try sign in again
      return await signIn({ username, password });
    }

    // For other errors, just rethrow
    throw error;
  }
};

/**
 * Sign out the current user
 * This function will sign out the user and clear all AWS credentials
 */
export const signOut = async (): Promise<void> => {
  try {
    await amplifySignOut();
    console.log("User signed out successfully");

    // Force clear any related storage
    if (typeof window !== "undefined" && window.localStorage) {
      const authKeys = Object.keys(localStorage).filter(
        (key) =>
          key.startsWith("amplify-signin") ||
          key.startsWith("CognitoIdentityServiceProvider") ||
          key.startsWith("aws.cognito") ||
          key.includes("credentials") ||
          key.includes("identity") ||
          key.includes("aws") ||
          key.includes("Amplify")
      );

      authKeys.forEach((key) => localStorage.removeItem(key));
      console.log(
        `Cleared ${authKeys.length} auth-related local storage items`
      );
    }

    // Force a page reload to clear any in-memory credentials
    if (typeof window !== "undefined") {
      window.location.href = AUTH_ROUTE;
    }
  } catch (error) {
    console.error("Error signing out:", error);
    toast.error("Sign out failed. Please try again.");
    throw new Error("Sign out failed");
  }
};

/**
 * Force sign out and clean up any corrupted auth state
 * This function will clear all authentication-related data from the browser
 * and force a clean sign out from Amplify Auth.
 */
export const forceCleanSignOut = async (): Promise<void> => {
  try {
    console.log("Forcing clean sign out...");

    // Try to sign out normally first
    try {
      await amplifySignOut();
    } catch (e) {
      console.warn("Normal sign out failed during cleanup:", e);
      // Continue with cleanup even if normal sign out fails
    }

    // Clear browser storage - be careful to only clear auth-related items
    if (typeof window !== "undefined" && window.localStorage) {
      // Clear Amplify-specific storage items and AWS credentials
      const authKeys = Object.keys(localStorage).filter(
        (key) =>
          key.startsWith("amplify-signin") ||
          key.startsWith("CognitoIdentityServiceProvider") ||
          key.startsWith("aws.cognito") ||
          key.includes("credentials") ||
          key.includes("identity") ||
          key.includes("aws") ||
          key.includes("Amplify")
      );

      authKeys.forEach((key) => localStorage.removeItem(key));
      console.log(
        `Cleared ${authKeys.length} auth-related local storage items`
      );
    }

    console.log("Auth state cleanup completed");
  } catch (error) {
    console.error("Error during auth state cleanup:", error);
    throw new Error("Failed to clean up authentication state");
  }
};

/**
 * Check if a user is currently authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    console.log("Checking if user is authenticated...");
    const session = await fetchAuthSession();
    if (!session) {
      console.error("Failed to fetch authentication session");
      return false;
    }

    console.log("Auth session:", session);
    console.log(
      "Auth session result:",
      JSON.stringify(
        {
          hasTokens: !!session.tokens,
          hasIdentityId: !!session.identityId,
        },
        null,
        2
      )
    );

    const isAuth = !!session.tokens;
    console.log("User is authenticated:", isAuth);
    return isAuth;
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

/**
 * Enhanced authentication check that detects and handles corrupted auth state
 * This function extends the standard isAuthenticated check to detect and
 * handle cases where the auth state is corrupted.
 */
export const enhancedIsAuthenticated = async (): Promise<{
  isAuthenticated: boolean;
  hasAuthError: boolean;
  errorMessage?: string;
}> => {
  try {
    console.log("Enhanced auth check: Checking if user is authenticated...");
    const session = await fetchAuthSession();
    if (!session) {
      console.error("Failed to fetch authentication session");
      return {
        isAuthenticated: false,
        hasAuthError: true,
        errorMessage: "Failed to fetch authentication session",
      };
    }

    console.log(
      "Enhanced auth check: Session result:",
      JSON.stringify(
        {
          hasTokens: !!session.tokens,
          hasIdentityId: !!session.identityId,
        },
        null,
        2
      )
    );

    const isAuth = !!session.tokens;

    // If we have no tokens, check if we might have corrupted auth state
    if (!isAuth) {
      try {
        // This is a simple test to check for UserAlreadyAuthenticatedException
        await amplifyGetCurrentUser();
        // If we get here without error, there's a valid user but no tokens
        // This is inconsistent and suggests corrupted auth state
        return {
          isAuthenticated: false,
          hasAuthError: true,
          errorMessage:
            "Authentication state appears to be corrupted. Please reset your authentication.",
        };
      } catch (userError) {
        // If this error contains "already signed in" but we have no tokens,
        // we likely have a corrupted auth state
        if (
          userError instanceof Error &&
          (userError.message.includes("already a signed in") ||
            userError.name === "UserAlreadyAuthenticatedException")
        ) {
          return {
            isAuthenticated: false,
            hasAuthError: true,
            errorMessage:
              "Authentication state appears to be corrupted. Please reset your authentication.",
          };
        }
      }
    }

    // Normal case - auth state is consistent
    return {
      isAuthenticated: isAuth,
      hasAuthError: false,
    };
  } catch (error) {
    console.error("Enhanced auth check: Error checking authentication:", error);

    // Check if this is an invalid token error
    if (
      error instanceof Error &&
      (error.message.includes("Invalid login token") ||
        error.message.includes("Couldn't verify signed token"))
    ) {
      return {
        isAuthenticated: false,
        hasAuthError: true,
        errorMessage:
          "Your authentication token is invalid. Please reset your authentication.",
      };
    }

    // Other errors
    return {
      isAuthenticated: false,
      hasAuthError: false,
    };
  }
};

/**
 * Get the current authenticated user
 * Extracts user identity from Cognito tokens including the 'sub' claim
 * which is the unique Cognito User ID used by API Gateway and Lambda
 *
 * @returns The current authenticated user data or null if not authenticated
 */
export const getCurrentUser = async () => {
  try {
    const session = await fetchAuthSession();
    if (!session || !session.tokens) {
      console.error("Failed to fetch authentication session or no tokens");
      return null;
    }

    // Extract the ID token and decode claims
    const idToken = session.tokens.idToken;
    let userId = session.identityId; // Fallback
    let email = null;
    let username = null;

    // Parse the JWT to get the 'sub' claim which is the Cognito User ID
    if (idToken) {
      try {
        // Decode the JWT payload (second part of token)
        const payload = JSON.parse(atob(idToken.toString().split(".")[1]));
        userId = payload.sub; // This is what API Gateway passes to Lambda
        email = payload.email;
        username = payload["cognito:username"];

        console.log("Extracted user identity from token:", {
          sub: userId,
          email,
          username,
        });
      } catch (e) {
        console.error("Error parsing ID token:", e);
      }
    }

    return {
      isAuthenticated: true,
      tokens: session.tokens,
      userId: userId, // Now using the 'sub' claim
      email: email,
      username: username,
      idToken: idToken?.toString(),
    };
  } catch (error) {
    console.error("Get current user error:", error);
    return null;
  }
};

/**
 * Get the current session
 */
export const getCurrentSession = async (): Promise<unknown> => {
  try {
    return await fetchAuthSession();
  } catch (error) {
    console.error("Error getting current session:", error);
    throw new Error("Failed to get current session");
  }
};

/**
 * Complete new password challenge
 *
 * This is used when a user is required to change their password on first login
 * or after an admin has reset their password.
 * 
 * @param newPassword The new password the user wants to set
 * @returns Object with success status and user data
 */
export const completeNewPasswordChallenge = async (
  newPassword: string
): Promise<{ success: boolean; user: unknown }> => {
  try {
    console.log("Completing new password challenge...");
    const result = await confirmSignIn({
      challengeResponse: newPassword,
    });
    console.log("New password challenge completed:", result);

    // Refresh credentials after successful password change
    try {
      await refreshCredentials();
      console.log("Credentials refreshed after password change");
    } catch (refreshError) {
      console.warn(
        "Failed to refresh credentials after password change:",
        refreshError
      );
    }

    toast.success("Password changed successfully! Welcome!");

    return {
      success: true,
      user: result,
    };
  } catch (error: any) {
    console.error("Error completing new password challenge:", error);

    const errorMessage = error.message || error.toString();

    if (error.name === "InvalidPasswordException" || errorMessage.includes("Password does not conform")) {
      toast.error("Password doesn't meet requirements. It should include uppercase, lowercase, numbers, and be at least 8 characters.");
    } else if (error.name === "NotAuthorizedException") {
      toast.error("Session expired. Please sign in again.");
    } else if (errorMessage.includes("Network Error")) {
      toast.error("Network error. Please check your internet connection.");
    } else {
      toast.error("Failed to change password. Please try again.");
    }

    throw new Error(
      error instanceof Error
        ? `Failed to complete password challenge: ${error.message}`
        : "Failed to complete password challenge"
    );
  }
};

/**
 * Sign up a new user with Cognito
 *
 * @param username Username for authentication
 * @param password User's password
 * @param email User's email
 * @param schoolCode School code for association on backend
 * @returns Sign-up result
 */
export const signUp = async ({
  username,
  password,
  email
}: {
  username: string;
  password: string;
  email: string;
}) => {
  try {
    const result = await amplifySignUp({
      username,
      password,
      options: {
        userAttributes: {
          email,
        },
        autoSignIn: { enabled: true },
      },
    });

    console.log("Sign up successful");
    return {
      isSignedUp: true,
      user: result,
    };
  } catch (error) {
    console.error("Error signing up:", error);
    handleAuthError(error);
    throw error;
  }
};

/**
 * Complete sign-up and sign-in flow
 * This function will sign up the user and then automatically sign them in
 *
 * @param username Username for authentication
 * @param password User's password
 * @param email User's email
 * @param schoolCode School code for association
 * @returns Sign-in result
 */
export const signUpAndSignIn = async ({
  username,
  password,
  email
}: {
  username: string;
  password: string;
  email: string;
}) => {
  try {
    // Sign up with school code as a custom attribute
    await signUp({ username, password, email });

    // Verify user through Lambda (replaces email confirmation)
    await verifyUser(username);

    // Sign in after verification
    const signInResult = await signIn({ username, password });

    return signInResult;
  } catch (error) {
    console.error("Sign up and sign in flow failed:", error);
    handleAuthError(error);
    throw error;
  }
};

/**
 * Handle authentication errors with user-friendly messages
 */
const handleAuthError = (error: any) => {
  const errorMessage = error.message || String(error);

  // Map Cognito error codes to user-friendly messages
  if (
    error.name === "UserNotConfirmedException" ||
    errorMessage.includes("User is not confirmed")
  ) {
    toast.error("Account verification required. Please try signing up again.");
  } else if (
    error.name === "NotAuthorizedException" ||
    errorMessage.includes("Incorrect username or password")
  ) {
    toast.error("Incorrect username or password.");
  } else if (
    error.name === "UserNotFoundException" ||
    errorMessage.includes("User does not exist")
  ) {
    toast.error("This email has not been registered.");
  } else if (errorMessage.includes("Password attempts exceeded")) {
    toast.error(
      "Too many failed attempts. Try again later or reset your password."
    );
  } else if (
    error.name === "UserAlreadyAuthenticatedException" ||
    errorMessage.includes("already a signed in user")
  ) {
    toast.error("You are already signed in. Please sign out first.");
    // We could also handle this case by calling forceCleanSignOut and retrying
  } else if (
    errorMessage.includes("Invalid login token") ||
    errorMessage.includes("JWT")
  ) {
    toast.error("Your login session has expired. Please sign in again.");
  } else if (errorMessage.includes("Network Error")) {
    toast.error("Network error. Please check your internet connection.");
  } else if (
    errorMessage.includes("UsernameExistsException") ||
    errorMessage.includes("User already exists")
  ) {
    toast.error("This email has already been registered. Redirecting to sign in page...");
    setTimeout(() => {
      window.location.href = AUTH_ROUTE;
    }
    , 2000);
  } else if (errorMessage.includes("InvalidPasswordException")) {
    toast.error(
      "Password doesn't meet requirements. It should include uppercase, lowercase, numbers, and be at least 8 characters."
    );
  } else if (
    errorMessage.includes("InvalidParameterException") &&
    errorMessage.includes("username")
  ) {
    toast.error(
      "Invalid username format. Please use only letters, numbers, and underscores."
    );
  } else if (errorMessage.includes("CodeMismatchException")) {
    toast.error("Invalid verification code. Please try again.");
  } else if (errorMessage.includes("ExpiredCodeException")) {
    toast.error("Verification code has expired. Please request a new one.");
  } else {
    toast.error("Authentication error. Please try again.");
  }
};

/**
 * Request password reset code
 * 
 * @param username User's email/username
 * @returns Object with success status and delivery details
 */
export const resetPassword = async (username: string) => {
  try {
    const output = await amplifyResetPassword({ username });
    console.log("Password reset code sent:", output);
    
    return {
      success: true,
      codeDeliveryDetails: output.nextStep.codeDeliveryDetails,
    };
  } catch (error: any) {
    console.error("Reset password error:", error);
    
    // Handle specific errors
    const errorMessage = error.message || error.toString();
    
    if (error.name === "UserNotFoundException" || errorMessage.includes("User does not exist")) {
      toast.error("This email has not been registered.");
    } else if (error.name === "LimitExceededException" || errorMessage.includes("Attempt limit exceeded")) {
      toast.error("Too many attempts. Please try again later.");
    } else if (errorMessage.includes("Network Error")) {
      toast.error("Network error. Please check your internet connection.");
    } else {
      toast.error("Failed to send reset code. Please try again.");
    }
    
    throw error;
  }
};

/**
 * Confirm password reset with verification code
 * 
 * @param params Object containing username, confirmation code, and new password
 * @returns Success status
 */
export const confirmResetPassword = async ({
  username,
  confirmationCode,
  newPassword,
}: {
  username: string;
  confirmationCode: string;
  newPassword: string;
}) => {
  try {
    await amplifyConfirmResetPassword({
      username,
      confirmationCode,
      newPassword,
    });
    
    console.log("Password reset successful");
    toast.success("Password reset successful! You can now sign in with your new password.");
    
    return { success: true };
  } catch (error: any) {
    console.error("Confirm reset password error:", error);
    
    // Handle specific errors
    const errorMessage = error.message || error.toString();
    
    if (error.name === "CodeMismatchException" || errorMessage.includes("Invalid verification code")) {
      toast.error("Invalid verification code. Please try again.");
    } else if (error.name === "ExpiredCodeException" || errorMessage.includes("code has expired")) {
      toast.error("Verification code has expired. Please request a new one.");
    } else if (error.name === "InvalidPasswordException" || errorMessage.includes("Password does not conform")) {
      toast.error("Password doesn't meet requirements. It should include uppercase, lowercase, numbers, and be at least 8 characters.");
    } else if (error.name === "LimitExceededException" || errorMessage.includes("Attempt limit exceeded")) {
      toast.error("Too many attempts. Please try again later.");
    } else if (errorMessage.includes("Network Error")) {
      toast.error("Network error. Please check your internet connection.");
    } else {
      toast.error("Failed to reset password. Please try again.");
    }
    
    throw error;
  }
};
